var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PlanTypeCategory } from '../../enum';
import { useTerminologyTranslation } from '../app/terminology';
import { ErrorMessageBox, FallbackComponent } from '../common';
import { ActionType } from '../report/constants';
import { ForecastExportDialog } from '../report/forecastExportDialog';
import { useReportAction } from '../report/hooks';
import { useReportAPI } from '../report/selectors';
import { useIsCompletedSimulation } from '../toastify/selector';
import { useDailyContext } from './context';
import { DailyChart } from './dailyChart';
import { DailyDetailTable } from './dailyDetailTable';
import { DailyForm } from './dailyForm';
import { Legend } from './detailTable';
import { IntervalDetailTable } from './intervalDetailTable';
import { ProdHoursDetailTable } from './prodHoursDetailTable';
import { useDailyAPI, useExportAPI, usePrintAPI, useWAPQueuesAPI } from './selectors';
export var DailyReportWrapper = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var viewType = _a.viewType;
    var t = useTerminologyTranslation('report', { keyPrefix: 'daily' }).t;
    var _k = useReportAPI(), reportData = _k[0], fetchReportData = _k[1];
    var queuesData = useWAPQueuesAPI()[0];
    var printData = usePrintAPI()[0];
    var _l = useExportAPI(viewType), exportData = _l[0], fetchExportData = _l[1], clearExportData = _l[2];
    var _m = useDailyAPI(), dailyData = _m[0], fetchDailyData = _m[1], clearDailyData = _m[2];
    var state = useDailyContext()[0];
    var _o = useState(false), dialogOpen = _o[0], setDialogOpen = _o[1];
    var _p = useState(), readyViewType = _p[0], setReadyViewType = _p[1];
    var basePlanId = useParams().basePlanId;
    var isCompletedSimulation = useIsCompletedSimulation(basePlanId);
    var tableRequestKey = [
        viewType,
        state === null || state === void 0 ? void 0 : state.uniqueId,
        state === null || state === void 0 ? void 0 : state.queueId,
        state === null || state === void 0 ? void 0 : state.chartQueueId,
        (_b = state === null || state === void 0 ? void 0 : state.chartQueueIds) === null || _b === void 0 ? void 0 : _b.join(','),
        state === null || state === void 0 ? void 0 : state.month,
        (_c = reportData.data) === null || _c === void 0 ? void 0 : _c.planTypeCategory,
        isCompletedSimulation,
    ].join('|');
    var pendingTableRequestKey = useRef(undefined);
    var tableRequestStarted = useRef(false);
    var _q = useState(), completedTableRequestKey = _q[0], setCompletedTableRequestKey = _q[1];
    var handleOpenExportDialog = useCallback(function () {
        setDialogOpen(true);
    }, []);
    var handleCloseExportDialog = useCallback(function () {
        setDialogOpen(false);
    }, []);
    var handleFormReady = useCallback(function () {
        setReadyViewType(viewType);
    }, [viewType]);
    useReportAction(ActionType.Export, handleOpenExportDialog, exportData.requesting, !((_e = (_d = reportData.data) === null || _d === void 0 ? void 0 : _d.scenarios) === null || _e === void 0 ? void 0 : _e.length));
    useEffect(function () { return clearExportData; }, [clearExportData]);
    var handleExportSubmit = useCallback(function (selection) {
        var selectionParams = selection.viewType === 'daily'
            ? { startMonth: selection.startPeriod, endMonth: selection.endPeriod }
            : { exportDate: selection.exportDate };
        fetchExportData(undefined, __assign({ planId: selection.planId, queuesId: selection.queueIds.join(','), viewType: selection.viewType }, selectionParams));
    }, [fetchExportData]);
    useEffect(function () {
        fetchReportData();
    }, [fetchReportData, isCompletedSimulation]);
    useEffect(function () {
        if (readyViewType !== viewType || (state === null || state === void 0 ? void 0 : state.viewType) !== viewType)
            return;
        fetchDailyData();
        return clearDailyData;
    }, [clearDailyData, fetchDailyData, isCompletedSimulation, readyViewType, state === null || state === void 0 ? void 0 : state.viewType, viewType]);
    useEffect(function () {
        if (dailyData.requesting) {
            pendingTableRequestKey.current = tableRequestKey;
            tableRequestStarted.current = true;
            return;
        }
        if (tableRequestStarted.current) {
            tableRequestStarted.current = false;
            setCompletedTableRequestKey(pendingTableRequestKey.current);
        }
    }, [dailyData.requesting, tableRequestKey]);
    var isProdHoursPlan = ((_f = reportData.data) === null || _f === void 0 ? void 0 : _f.planTypeCategory) === PlanTypeCategory.ProdHours;
    var detailTable = (function () {
        if (isProdHoursPlan) {
            return _jsx(ProdHoursDetailTable, {});
        }
        if (viewType === 'interval') {
            return _jsx(IntervalDetailTable, {});
        }
        return _jsx(DailyDetailTable, {});
    })();
    return (_jsxs(_Fragment, { children: [_jsx(ErrorMessageBox, { errors: [queuesData.error, dailyData.error, printData.error, exportData.error] }), _jsx(DailyForm, { viewType: viewType, onReady: handleFormReady }), _jsxs(Box, __assign({ sx: { mt: 4 } }, { children: [_jsx(DailyChart, { tableReady: completedTableRequestKey === tableRequestKey }), _jsxs(FallbackComponent, __assign({ requesting: !!reportData.requesting || !!queuesData.requesting || !!dailyData.requesting }, { children: [_jsxs(Stack, __assign({ direction: "row", alignItems: "center", gap: 1, position: "absolute" }, { children: [_jsx(Legend, { children: "-" }), _jsxs(Typography, { children: ["= ", t('queueClosed')] })] })), detailTable] }))] })), _jsx(ForecastExportDialog, { open: dialogOpen, viewType: viewType, activeDate: state === null || state === void 0 ? void 0 : state.activeDate, queuesState: queuesData, onClose: handleCloseExportDialog, onSubmit: handleExportSubmit, defaultPlanId: Number((_h = (_g = state === null || state === void 0 ? void 0 : state.uniqueId) === null || _g === void 0 ? void 0 : _g.split('-')[0]) !== null && _h !== void 0 ? _h : (_j = reportData.data) === null || _j === void 0 ? void 0 : _j.id) })] }));
};
export var DailyReport = function () {
    return _jsx(DailyReportWrapper, { viewType: "daily" });
};
export var IntervalReport = function () {
    return _jsx(DailyReportWrapper, { viewType: "interval" });
};
